import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { post } from '../lib/api';
import { useAtomValue } from 'jotai';
import { selGuildAtom } from '../atoms/guild';

interface OptimaLinkStatus {
  ok: boolean;
  online: boolean;
  version: string | null;
  ping_ms: number | null;
  players: number;
  playing_players: number;
  memory_used: number;
  memory_free: number;
  cpu_system: number | null;
  error: string | null;
}

interface QueueTrack { title: string; url?: string; requester?: string; }
interface MusicState {
  now_playing: string | null;
  volume: number;
  loop: boolean;
  queue: QueueTrack[];
}
interface VoiceChannel { id: string; name: string; }
interface Guild { id: string; name: string; icon?: string; voice_channels?: VoiceChannel[]; music?: MusicState; }
interface DashData { guilds?: Guild[]; lavalink?: OptimaLinkStatus; }

const CTRL_BTN = 'px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] active:scale-[0.97] text-xs font-bold uppercase tracking-wider text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5';
const GREEN_BTN = 'px-6 py-3 bg-cyberGreen hover:bg-cyberGreen/90 active:scale-[0.97] text-slate-950 font-black text-xs rounded-xl uppercase tracking-wider transition-all shadow-lg shadow-cyberGreen/10 disabled:opacity-40 flex items-center gap-2';
const isSameChans = (a: VoiceChannel[], b: VoiceChannel[]) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id || a[i].name !== b[i].name) return false;
  }
  return true;
};

export function OptimaLinkPage() {
  const [guilds,       setGuilds]       = useState<Guild[]>([]);
  const selGuild = useAtomValue(selGuildAtom);
  const selGuildRef = useRef(selGuild);
  const [music,        setMusic]        = useState<MusicState | null>(null);
  const [nodeStatus,   setNodeStatus]   = useState<OptimaLinkStatus | null>(null);
  const [voiceChans,   setVoiceChans]   = useState<VoiceChannel[]>([]);
  const [selVoice,     setSelVoice]     = useState<string>('');
  const [selReqChan,   setSelReqChan]   = useState<string>('');
  const [query,        setQuery]        = useState('');
  const [bass,         setBass]         = useState(0);
  const [treble,       setTreble]       = useState(0);
  const [loudness,     setLoudness]     = useState(100);
  const [studioStatus, setStudioStatus] = useState('Volume updates live. EQ applies to upcoming playback.');
  const [voiceStatus,  setVoiceStatus]  = useState('');
  const [reqStatus,    setReqStatus]    = useState('');
  const [pbStatus,     setPbStatus]     = useState('');
  const [nodeLoading,  setNodeLoading]  = useState(false);
  const [nodeTesting,  setNodeTesting]  = useState(false);
  const [cfSeconds,    setCfSeconds]    = useState(5.0);
  const [cfCurve,      setCfCurve]      = useState('EQUAL_POWER');
  const [cfStatus,     setCfStatus]     = useState('');
  const [cfApplying,   setCfApplying]   = useState(false);
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    selGuildRef.current = selGuild;
  }, [selGuild]);

  // Load dashboard data (guilds + music state)
  const loadDashboard = async (guildId?: string) => {
    try {
      const data = await post<DashData>('/panel/stats');
      if (data.guilds) {
        setGuilds(data.guilds);
        const gid = guildId || selGuildRef.current || data.guilds[0]?.id || '';
        const guild = data.guilds.find(g => g.id === gid);
        if (guild) {
          setMusic(guild.music ?? null);
          const chans = guild.voice_channels ?? [];
          setVoiceChans(prev => isSameChans(prev, chans) ? prev : chans);
          setSelVoice(prev => {
            const hasPrev = chans.some(c => c.id === prev);
            return hasPrev ? prev : (chans[0]?.id || '');
          });
          setSelReqChan(prev => {
            const hasPrev = chans.some(c => c.id === prev);
            return hasPrev ? prev : (chans[0]?.id || '');
          });
        }
      }
      if (data.lavalink) setNodeStatus(data.lavalink);
    } catch { /* silent */ }
  };

  const fetchNodeStatus = async () => {
    setNodeLoading(true);
    try {
      const data = await post<OptimaLinkStatus>('/panel/lavalink');
      setNodeStatus(data);
    } catch (err: unknown) {
      toast.error('Failed to fetch OptimaLink status: ' + (err instanceof Error ? err.message : 'error'));
    } finally { setNodeLoading(false); }
  };

  const loadCrossfade = async (guildId?: string) => {
    const gid = guildId || selGuild;
    if (!gid) return;
    try {
      const data = await post<{ ok: boolean; seconds: number; curve: string }>('/panel/crossfade', { action: 'get', guild_id: gid });
      if (data.ok) {
        setCfSeconds(data.seconds);
        setCfCurve(data.curve);
      }
    } catch { /* silent */ }
  };

  const handleApplyCrossfade = async () => {
    if (!selGuild) { setCfStatus('Select a server first.'); return; }
    setCfApplying(true);
    setCfStatus('Applying...');
    try {
      const data = await post<{ ok: boolean; error?: string }>('/panel/crossfade', {
        action: 'set', guild_id: selGuild, seconds: cfSeconds, curve: cfCurve,
      });
      setCfStatus(data.ok ? `Crossfade set — ${cfSeconds}s ${cfCurve}` : (data.error ?? 'Failed.'));
    } catch (err: unknown) {
      setCfStatus(err instanceof Error ? err.message : 'Failed to apply crossfade.');
    } finally { setCfApplying(false); }
  };

  useEffect(() => {
    loadDashboard();
    fetchNodeStatus();
    refreshRef.current = setInterval(() => loadDashboard(), 6000);
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadCrossfade(); }, [selGuild]); // eslint-disable-line react-hooks/exhaustive-deps



  const handleJoinVoice = async () => {
    setVoiceStatus('Requesting join...');
    try {
      const data = await post<{ message: string }>('/panel/join', { guild_id: selGuild, channel_id: selVoice });
      setVoiceStatus(data.message || 'Joined.');
    } catch (err: unknown) { setVoiceStatus(err instanceof Error ? err.message : 'Unable to join voice.'); }
  };

  const handleRequestSong = async () => {
    if (!query.trim()) { setReqStatus('Enter a track to queue.'); return; }
    setReqStatus('Queuing...');
    try {
      const data = await post<{ message: string }>('/panel/request', {
        guild_id: selGuild, query: query.trim(), channel_id: selReqChan, bass, treble, loudness,
      });
      setReqStatus(data.message || 'Queued.');
      setQuery('');
      await loadDashboard(selGuild);
    } catch (err: unknown) { setReqStatus(err instanceof Error ? err.message : 'Request failed.'); }
  };

  const handleApplyMix = async () => {
    if (!selGuild) { setStudioStatus('Select a server first.'); return; }
    setStudioStatus('Applying studio mix...');
    try {
      const data = await post<{ message: string }>('/panel/control', { action: 'apply_mix', guild_id: selGuild, bass, treble, loudness });
      setStudioStatus(data.message || 'Studio mix applied.');
      await loadDashboard(selGuild);
    } catch (err: unknown) { setStudioStatus(err instanceof Error ? err.message : 'Unable to apply studio mix.'); }
  };

  const handleControl = async (action: string) => {
    if (!selGuild) { setPbStatus('Select a server first.'); return; }
    setPbStatus(`Running ${action}...`);
    try {
      const data = await post<{ message: string }>('/panel/control', { action, guild_id: selGuild });
      setPbStatus(data.message || 'Control action sent.');
      await loadDashboard(selGuild);
    } catch (err: unknown) { setPbStatus(err instanceof Error ? err.message : 'Control action failed.'); }
  };

  const handleTestNode = async () => {
    setNodeTesting(true);
    try {
      const data = await post<OptimaLinkStatus>('/panel/lavalink');
      setNodeStatus(data);
      toast.success(data.online ? `Online — ${data.ping_ms}ms` : 'Node offline');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Connection test failed.');
    } finally { setNodeTesting(false); }
  };

  const fmtDb  = (v: number) => (v > 0 ? '+' : '') + v.toFixed(1) + ' dB';
  const isOnline = nodeStatus?.online ?? false;
  const memUsed  = nodeStatus ? (nodeStatus.memory_used  / 1048576).toFixed(0) : '0';
  const memFree  = nodeStatus ? (nodeStatus.memory_free  / 1048576).toFixed(0) : '0';
  const memTotal = nodeStatus ? ((nodeStatus.memory_used + nodeStatus.memory_free) / 1048576).toFixed(0) : '0';
  const memPct   = nodeStatus && (nodeStatus.memory_used + nodeStatus.memory_free) > 0
    ? Math.round((nodeStatus.memory_used / (nodeStatus.memory_used + nodeStatus.memory_free)) * 100) : 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[9px] font-mono tracking-[0.2em] text-slate-600 uppercase mb-1">Management</div>
          <h1 className="text-2xl font-black uppercase tracking-tight text-white">
            OptimaLink <span className="text-cyberGreen neon-text-glow">Studio</span>
          </h1>
        </div>
        <button onClick={() => loadDashboard(selGuild)} className="px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] text-slate-400 hover:text-white text-xs transition-all" title="Refresh">
          <i className="fa-solid fa-rotate" />
        </button>
      </div>

      {/* ── EQ Studio Card ── */}
      <div className="glass-card rounded-2xl p-6 flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-black text-lg text-white">Voice EQ Studio</h3>
            <p className="text-xs text-slate-400 mt-0.5">Apply real-time equalization & audio filters to the bot's speech output.</p>
          </div>
          <div className="w-8 h-8 rounded-lg bg-cyberGreen/10 border border-cyberGreen/20 flex items-center justify-center text-cyberGreen">
            <i className="fa-solid fa-sliders" />
          </div>
        </div>

        {/* EQ Sliders */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white/[0.02] p-4 rounded-xl border border-white/[0.05]">
          {[
            { label: 'Sub-Bass',     val: bass,     set: setBass,     min: -20, max: 20,  step: 0.5, fmt: fmtDb },
            { label: 'High Treble',  val: treble,   set: setTreble,   min: -20, max: 20,  step: 0.5, fmt: fmtDb },
            { label: 'Master Level', val: loudness, set: setLoudness, min: 0,   max: 200, step: 5,   fmt: (v: number) => v + '%' },
          ].map(({ label, val, set, min, max, step, fmt }) => (
            <div key={label} className="flex flex-col gap-2">
              <div className="flex justify-between text-xs font-mono">
                <span className="text-slate-400">{label}</span>
                <span className="text-cyberGreen font-semibold">{fmt(val)}</span>
              </div>
              <input
                type="range" min={min} max={max} step={step} value={val}
                onChange={e => set(Number(e.target.value))}
                className="w-full accent-[#00ff66] cursor-pointer"
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-start">
          <button onClick={handleApplyMix} className={GREEN_BTN}>
            <i className="fa-solid fa-wand-magic-sparkles" /> Apply Studio Mix
          </button>
          <p className="text-xs font-mono text-slate-500 flex items-center mt-1">{studioStatus}</p>
        </div>

        {/* Join Voice */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/[0.05]">
          <select
            value={selVoice}
            onChange={e => setSelVoice(e.target.value)}
            className="flex-1 bg-slate-900 border border-white/10 rounded-xl px-3 py-3 text-xs text-slate-300 font-mono focus:outline-none focus:border-cyberGreen/30 transition-colors"
          >
            {voiceChans.length === 0
              ? <option value="">No accessible voice channels</option>
              : voiceChans.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
            }
          </select>
          <button onClick={handleJoinVoice} disabled={!selGuild || !selVoice} className={GREEN_BTN}>
            <i className="fa-solid fa-phone" /> Join Voice
          </button>
        </div>
        {voiceStatus && <p className="text-xs font-mono text-slate-500">{voiceStatus}</p>}
      </div>

      {/* ── Music Player Card ── */}
      <div className="glass-card rounded-2xl p-6 flex flex-col gap-5">
        <div className="flex justify-between items-start">
          <div>
            <span className="text-[9px] font-mono uppercase tracking-widest text-cyberGreen font-bold bg-cyberGreen/10 px-2 py-0.5 rounded">NOW STREAMING</span>
            <h3 className="font-black text-lg text-white mt-1.5">{music?.now_playing ?? '(none)'}</h3>
            <p className="text-slate-400 text-xs font-mono mt-0.5">
              Volume: {music?.volume ?? '--'} | Loop: {music?.loop ? 'ON' : 'OFF'}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyberGreen animate-ping" />
            <i className="fa-solid fa-volume-high text-cyberGreen text-sm" />
          </div>
        </div>

        {/* Queue */}
        <div className="bg-white/[0.02] rounded-xl p-4 border border-white/[0.05]">
          <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400 block mb-2">Music Queue</span>
          <div className="space-y-1.5 max-h-32 overflow-y-auto scrollbar-thin">
            {(!music?.queue || music.queue.length === 0)
              ? <div className="text-xs text-slate-600 font-mono">(empty queue)</div>
              : music.queue.map((track, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] font-mono text-slate-300">
                  <span className="text-slate-600 w-4 shrink-0">{i + 1}.</span>
                  <span className="truncate">{track.title}</span>
                  {track.requester && <span className="text-slate-500 shrink-0">· {track.requester}</span>}
                </div>
              ))
            }
          </div>
        </div>

        {/* Playback Controls */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button onClick={() => handleControl('pause')}        className={CTRL_BTN}><i className="fa-solid fa-pause"        />Pause</button>
          <button onClick={() => handleControl('resume')}       className={CTRL_BTN}><i className="fa-solid fa-play"         />Resume</button>
          <button onClick={() => handleControl('skip')}         className={CTRL_BTN}><i className="fa-solid fa-forward-step" />Skip</button>
          <button onClick={() => handleControl('toggle_loop')}  className={CTRL_BTN}><i className="fa-solid fa-repeat"       />Loop</button>
          <button onClick={() => handleControl('shuffle')}      className={CTRL_BTN}><i className="fa-solid fa-shuffle"      />Shuffle</button>
          <button onClick={() => handleControl('clear_queue')}  className={CTRL_BTN}><i className="fa-solid fa-trash-can"    />Clear</button>
          <button onClick={() => handleControl('stop')}
            className="col-span-2 px-4 py-3 rounded-xl border border-rose-500/20 bg-rose-500/10 hover:bg-rose-500/20 active:scale-[0.97] text-xs font-bold uppercase tracking-wider text-rose-300 transition-all flex items-center justify-center gap-1.5">
            <i className="fa-solid fa-stop" />Stop
          </button>
        </div>
        {pbStatus && <p className="text-xs font-mono text-slate-500">{pbStatus}</p>}

        {/* Crossfade */}
        <div className="flex flex-col gap-3 pt-4 border-t border-white/[0.05]">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400 block">Crossfade</span>
              <span className="text-[10px] font-mono text-slate-600">Smooth transition between tracks</span>
            </div>
            <div className="w-8 h-8 rounded-lg bg-cyberGreen/10 border border-cyberGreen/20 flex items-center justify-center text-cyberGreen">
              <i className="fa-solid fa-shuffle text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white/[0.02] p-4 rounded-xl border border-white/[0.05]">
            {/* Duration slider */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between text-xs font-mono">
                <span className="text-slate-400">Duration</span>
                <span className="text-cyberGreen font-semibold">{cfSeconds.toFixed(1)}s</span>
              </div>
              <input
                type="range" min={1} max={12} step={0.5} value={cfSeconds}
                onChange={e => setCfSeconds(Number(e.target.value))}
                className="w-full accent-[#00ff66] cursor-pointer"
              />
              <div className="flex justify-between text-[9px] font-mono text-slate-600">
                <span>1s</span><span>12s</span>
              </div>
            </div>
            {/* Curve selector */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-mono text-slate-400">Fade Curve</span>
              <select
                value={cfCurve}
                onChange={e => setCfCurve(e.target.value)}
                className="bg-slate-900 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-slate-300 font-mono focus:outline-none focus:border-cyberGreen/30 transition-colors"
              >
                <option value="EQUAL_POWER">Equal Power (natural)</option>
                <option value="LINEAR">Linear</option>
                <option value="EASE_IN">Ease In</option>
                <option value="EASE_OUT">Ease Out</option>
                <option value="LOG">Logarithmic</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleApplyCrossfade} disabled={cfApplying || !selGuild} className={GREEN_BTN + ' disabled:opacity-40'}>
              <i className={`fa-solid fa-arrows-spin ${cfApplying ? 'animate-spin' : ''}`} />
              Apply Crossfade
            </button>
            {cfStatus && <p className="text-xs font-mono text-slate-500">{cfStatus}</p>}
          </div>
        </div>

        {/* Search & Play */}
        <div className="flex flex-col gap-3 pt-4 border-t border-white/[0.05]">
          <div className="flex gap-2">
            <input
              type="text" value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRequestSong()}
              placeholder="Search track, artist, YouTube/SoundCloud URL..."
              className="flex-1 text-xs bg-slate-900 border border-white/10 rounded-xl px-4 py-3 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-cyberGreen/50 transition-colors font-mono"
            />
            <button onClick={handleRequestSong} disabled={!selGuild}
              className="w-11 h-11 bg-cyberGreen text-slate-950 hover:bg-cyberGreen/90 active:scale-[0.97] rounded-xl flex items-center justify-center transition-all shadow-lg shadow-cyberGreen/10 disabled:opacity-40 shrink-0">
              <i className="fa-solid fa-play text-sm" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[10px] font-mono text-slate-500 uppercase shrink-0">Target Channel</label>
            <select
              value={selReqChan} onChange={e => setSelReqChan(e.target.value)}
              className="flex-1 bg-slate-900 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-slate-300 font-mono focus:outline-none focus:border-cyberGreen/30 transition-colors"
            >
              {voiceChans.length === 0
                ? <option value="">No accessible voice channels</option>
                : voiceChans.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
              }
            </select>
          </div>
          {reqStatus && <p className="text-xs font-mono text-slate-500">{reqStatus}</p>}
        </div>
      </div>

      {/* ── OptimaLink Node Status Card ── */}
      <div className="glass-card rounded-2xl p-6 flex flex-col gap-5">
        <div className="flex justify-between items-start">
          <div>
            <span className="text-[9px] font-mono uppercase tracking-widest text-cyberGreen font-bold bg-cyberGreen/10 px-2 py-0.5 rounded">AUDIO ENGINE</span>
            <h3 className="font-black text-lg text-white mt-1.5">OptimaLink Server</h3>
            <p className="text-slate-400 text-xs font-mono mt-0.5">Internal audio processing node — health check & live diagnostics.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full transition-all duration-300 ${isOnline ? 'bg-cyberGreen animate-ping' : 'bg-slate-600'}`} />
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded font-bold
              ${isOnline ? 'bg-cyberGreen/10 text-cyberGreen' : 'bg-slate-800 text-slate-400'}`}>
              {nodeStatus ? (isOnline ? 'ONLINE' : 'OFFLINE') : 'UNKNOWN'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Version',        value: nodeStatus?.version ?? '-' },
            { label: 'Ping',           value: nodeStatus?.ping_ms != null ? nodeStatus.ping_ms + ' ms' : '-' },
            { label: 'Playing / Total',value: nodeStatus ? `${nodeStatus.playing_players} / ${nodeStatus.players}` : '-' },
            { label: 'CPU Load',       value: nodeStatus?.cpu_system != null ? (nodeStatus.cpu_system * 100).toFixed(1) + '%' : '-' },
          ].map(s => (
            <div key={s.label} className="bg-white/[0.02] rounded-xl p-3 border border-white/[0.05] flex flex-col gap-1">
              <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{s.label}</span>
              <span className="text-white font-mono font-bold text-sm">{s.value}</span>
            </div>
          ))}
        </div>

        {/* Memory bar */}
        <div className="flex flex-col gap-1.5">
          <div className="flex justify-between text-[10px] font-mono text-slate-500">
            <span>JVM Memory</span>
            <span>{memUsed} MB / {memTotal} MB</span>
          </div>
          <div className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
            <div className="h-full bg-cyberGreen rounded-full transition-all duration-500" style={{ width: memPct + '%' }} />
          </div>
        </div>

        {nodeStatus?.error && (
          <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-[10px] font-mono text-rose-400">
            <i className="fa-solid fa-triangle-exclamation mr-2" />{nodeStatus.error}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-white/[0.05]">
          <button onClick={handleTestNode} disabled={nodeTesting || nodeLoading} className={GREEN_BTN + ' disabled:opacity-50'}>
            <i className={`fa-solid fa-satellite-dish ${nodeTesting ? 'animate-spin' : ''}`} />
            Test Connection
          </button>
          <p className="text-xs font-mono text-slate-500">
            {nodeTesting ? 'Pinging OptimaLink node...' : `Click to ping the OptimaLink audio node.`}
          </p>
        </div>
      </div>
    </div>
  );
}
